import { z } from 'zod';

// Validation schema for phone numbers
const phoneNumberSchema = z.string().min(10).max(15);

/**
 * Formats a phone number to US format: (XXX) XXX-XXXX
 * Handles various input formats including E.164
 * @param phoneNumber Phone number in any format
 * @returns Formatted US phone number or null if invalid
 */
export function formatUSPhoneNumber(phoneNumber: string): string | null {
  try {
    // Remove all non-digits
    let digits = phoneNumber.replace(/\D/g, '');
    
    // Handle E.164 format by removing leading '1' if present
    if (digits.length === 11 && digits.startsWith('1')) {
      digits = digits.slice(1);
    }
    
    // Validate we have exactly 10 digits
    if (digits.length !== 10) {
      return null;
    }

    // Format as (XXX) XXX-XXXX
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  } catch (error) {
    return null;
  }
}

/**
 * Formats a phone number to E.164 format: +1XXXXXXXXXX
 * @param phoneNumber Phone number in any format
 * @returns E.164 formatted phone number or null if invalid
 */
export function formatE164(phoneNumber: string): string | null {
  try {
    // Remove all non-digits
    let digits = phoneNumber.replace(/\D/g, '');
    
    // Remove leading '1' if present, we'll add it back in E.164 format
    if (digits.length === 11 && digits.startsWith('1')) {
      digits = digits.slice(1);
    }
    
    // Validate we have exactly 10 digits
    if (digits.length !== 10) {
      return null;
    }
    
    // Format as +1XXXXXXXXXX
    return `+1${digits}`;
  } catch (error) {
    return null;
  }
}

/**
 * Validates if a string is a valid US phone number
 * @param phoneNumber Phone number to validate
 * @returns true if valid, false otherwise
 */
export function isValidUSPhoneNumber(phoneNumber: string): boolean {
  const formatted = formatUSPhoneNumber(phoneNumber);
  return formatted !== null;
}

/**
 * Formats a phone number as the user types
 * @param value Current input value
 * @returns Formatted phone number for display
 */
export function formatPhoneNumberInput(value: string): string {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  // Format the number as user types
  if (digits.length <= 3) {
    return digits;
  } else if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  } else {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
}

/**
 * Extracts a clean phone number from any format
 * @param phoneNumber Phone number in any format
 * @returns Clean 10-digit phone number or null if invalid
 */
export function cleanPhoneNumber(phoneNumber: string): string | null {
  try {
    // Remove all non-digits
    let digits = phoneNumber.replace(/\D/g, '');
    
    // Handle E.164 format
    if (digits.length === 11 && digits.startsWith('1')) {
      digits = digits.slice(1);
    }
    
    // Validate length
    if (digits.length !== 10) {
      return null;
    }
    
    return digits;
  } catch (error) {
    return null;
  }
}